import { useGetGenericImageSettings } from "@/api/user-settings";
import { GenericImagePreview } from "@/components/images/GenericImagePreview";
import { EmbedPlayer } from "@/components/media/EmbedPlayer";
import { ResponsiveVideo } from "@/components/media/ResponsiveVideo";
import { resolveEmbedThumbnailUrl } from "@/lib/media/embed";
import { CATALOG_FULLSCREEN_PLAYER_OVERRIDES } from "@/lib/media/catalogFullscreenPlayer";
import { selectAttachmentForPreview, TypeDefaultImage } from "@/lib/image";
import { cn } from "@/lib/utils";
import { Attachment } from "@/lib/types";
import { CATALOG_THUMBNAIL_MARKER } from "@/pages/dashboard/shared/catalogMediaThumbnail";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Play, Youtube } from "lucide-react";
import React from "react";
import { Dialog, DialogContent, DialogTrigger } from "../ui/dialog";

interface IMediaOrVideoProps {
  media: {
    attachments: Attachment[];
    type: TypeDefaultImage;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
  };
  /** Poster preview in trigger; fullscreen player with controls in dialog (catalog fiche parity). */
  catalogFullscreen?: boolean;
}

export const ImageFullscreen = ({
  media: { attachments, type, alt, width, height, className },
  catalogFullscreen = false,
}: IMediaOrVideoProps) => {
  const [open, setOpen] = React.useState(false);
  const { data: genericImageSettings } = useGetGenericImageSettings();

  const selectedAttachment = React.useMemo(
    () => selectAttachmentForPreview(attachments ?? [], type),
    [attachments, type],
  );
  const isGenericImage = selectedAttachment?.isGenericImage === true;

  // Align with front public: resolve a preview for image/video/embed.
  const previewUrl =
    selectedAttachment && !isGenericImage
      ? selectedAttachment.kind === "embed"
        ? resolveEmbedThumbnailUrl(
            selectedAttachment.embedProvider,
            selectedAttachment.embedVideoId,
            selectedAttachment.embedUrl,
          ) ?? null
        : selectedAttachment.kind === "video"
          ? selectedAttachment.posterPublicUrl ||
            selectedAttachment.mediumPublicUrl ||
            selectedAttachment.publicUrl ||
            selectedAttachment.largePublicUrl ||
            null
          : selectedAttachment.largePublicUrl ||
            selectedAttachment.publicUrl ||
            selectedAttachment.mediumPublicUrl ||
            null
      : null;
  const largeUrl =
    selectedAttachment?.kind === "video"
      ? selectedAttachment.videoPublicUrl || previewUrl
      : previewUrl;

  const isVideo =
    selectedAttachment?.kind === "video" &&
    !!selectedAttachment.videoPublicUrl;
  const isEmbed = selectedAttachment?.kind === "embed";
  const isVideoLike =
    catalogFullscreen && (isVideo || isEmbed || selectedAttachment?.kind === "video");
  const showPosterPreview = isVideoLike || isVideo || isEmbed;

  const isThumbnail = className?.includes(CATALOG_THUMBNAIL_MARKER) ?? false;
  const thumbnailMinSize = Math.min(width || 500, height || 500);
  const thumbnailFontScale = Math.max(0.35, Math.min(1, thumbnailMinSize / 500));
  const playIconClass =
    thumbnailMinSize <= 80 ? "h-3.5 w-3.5" : thumbnailMinSize <= 100 ? "h-4 w-4" : "h-5 w-5";
  const playButtonClass =
    thumbnailMinSize <= 80
      ? "h-7 w-7"
      : thumbnailMinSize <= 100
        ? "h-8 w-8"
        : "h-10 w-10";

  const littleContent = isGenericImage ? (
    <div
      className={`cursor-pointer ${className}`}
      style={{ width: width || 500, height: height || 500 }}
      aria-label={alt}
    >
      <GenericImagePreview
        settings={genericImageSettings}
        className="w-full h-full object-contain"
        fontScale={thumbnailFontScale}
      />
    </div>
  ) : showPosterPreview ? (
    <div
      className={cn(
        "relative cursor-pointer overflow-hidden",
        isThumbnail
          ? "flex h-full w-full items-center justify-center bg-transparent"
          : undefined,
        className,
      )}
      style={
        isThumbnail
          ? undefined
          : { width: width || 500, height: height || 500 }
      }
      aria-label={alt}
    >
      {previewUrl ? (
        <img
          draggable={false}
          src={previewUrl}
          alt={alt}
          width={width || 500}
          height={height || 500}
          className={
            isThumbnail
              ? "max-h-full max-w-full object-contain object-center"
              : "h-full w-full object-contain"
          }
        />
      ) : (
        <div
          className={cn(
            "flex items-center justify-center",
            isThumbnail
              ? "aspect-video h-full w-full max-h-full max-w-full"
              : "h-full w-full bg-black/80",
          )}
        >
          <Youtube className={cn(playIconClass, "text-white")} />
        </div>
      )}
      <div
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center",
          isThumbnail ? "" : "bg-black/25",
        )}
      >
        <span
          className={cn(
            "flex items-center justify-center rounded-full bg-black/55",
            playButtonClass,
          )}
        >
          <Play
            className={cn(playIconClass, "fill-white text-white pl-0.5")}
          />
        </span>
      </div>
    </div>
  ) : (
    <img
      draggable={false}
      src={previewUrl || ""}
      alt={alt}
      width={width || 500}
      height={height || 500}
      className={`object-contain cursor-pointer ${className}`}
    />
  );

  const content = isGenericImage ? (
    <div className="max-h-[90dvh] w-full h-full absolute top-0 left-0">
      <GenericImagePreview
        settings={genericImageSettings}
        className="w-full h-full object-contain"
      />
    </div>
  ) : isVideo && selectedAttachment?.videoPublicUrl ? (
    <ResponsiveVideo
      src={selectedAttachment.videoPublicUrl}
      poster={previewUrl || undefined}
      className="max-h-[90dvh] w-full bg-black absolute top-0 left-0"
    />
  ) : isEmbed ? (
    <div
      className={
        catalogFullscreen
          ? "mx-auto h-[80dvh] w-[min(90vw,1200px)]"
          : "absolute top-0 left-0 h-[80dvh] w-[85dvw]"
      }
    >
      <EmbedPlayer
        contain
        eager
        provider={selectedAttachment?.embedProvider}
        videoId={selectedAttachment?.embedVideoId}
        embedUrl={selectedAttachment?.embedUrl}
        className="h-full w-full"
        title={alt}
        playerOverrides={
          catalogFullscreen ? CATALOG_FULLSCREEN_PLAYER_OVERRIDES : undefined
        }
      />
    </div>
  ) : (
    <img
      draggable={false}
      src={largeUrl || previewUrl || ""}
      alt={alt}
      width={900}
      height={900}
      className="object-contain max-h-[90dvh] w-full absolute top-0 left-0"
    />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          aria-label={alt}
          className={cn(
            "relative cursor-pointer border-0 bg-transparent p-0",
            isThumbnail
              ? "flex h-full w-full items-center justify-center overflow-hidden"
              : // w-fit : le bouton épouse l'image (sinon w-full laisse l'image à gauche dans le drawer)
                "mx-auto flex w-fit max-w-full items-center justify-center",
          )}
        >
          {littleContent}
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-transparent border-none h-[95dvh] min-w-[90dvw] flex flex-col justify-center gap-y-2 outline-none"
        onInteractOutside={() => {
          setOpen(false);
        }}
        needCloseButton={false}
      >
        <div
          className={cn(
            "relative h-full w-full",
            isVideoLike ? "flex items-center justify-center" : undefined,
          )}
        >
          {open ? content : null}
          <div
            className="absolute right-[110px] top-[-20px] text-white cursor-pointer"
            onClick={() => setOpen(false)}
          >
            <Cross2Icon className="h-8 w-8" />
            <span className="sr-only">Close</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
